import { useState } from 'react';
import {
  Ruler,
  Square,
  Printer,
  Trash2,
  X,
  Hexagon,
  RectangleHorizontal,
  Circle,
  Spline,
  MapPin,
} from 'lucide-react';
import { measure } from '@/agent/tools/measure';
import { sketchTool } from '@/agent/tools/sketch';
import { printMap } from '@/agent/tools/print-map';
import {
  clearMeasurements,
  flashMeasurement,
  removeMeasurement,
} from '@/map/measurements';
import { useMeasurementsStore } from '@/state/measurementsStore';
import {
  clearSketches,
  flashSketch,
  removeSketch,
} from '@/map/sketches';
import { useSketchesStore, type SketchShape } from '@/state/sketchesStore';

type Busy =
  | null
  | 'distance'
  | 'area'
  | 'sketch:polygon'
  | 'sketch:rectangle'
  | 'sketch:circle'
  | 'sketch:polyline'
  | 'sketch:point'
  | 'export';

const SKETCH_SHAPES: { id: SketchShape; label: string; Icon: typeof Hexagon }[] = [
  { id: 'polygon', label: 'Polygon', Icon: Hexagon },
  { id: 'rectangle', label: 'Rectangle', Icon: RectangleHorizontal },
  { id: 'circle', label: 'Circle', Icon: Circle },
  { id: 'polyline', label: 'Polyline', Icon: Spline },
];

const SKETCH_ICON: Record<SketchShape, typeof Hexagon> = {
  polygon: Hexagon,
  rectangle: RectangleHorizontal,
  circle: Circle,
  polyline: Spline,
  point: MapPin,
};

export function ToolsPanel() {
  const [busy, setBusy] = useState<Busy>(null);
  const [hint, setHint] = useState<string | null>(null);
  const measurements = useMeasurementsStore((s) => s.entries);
  const sketches = useSketchesStore((s) => s.entries);

  const startMeasure = async (mode: 'distance' | 'area') => {
    if (busy) return;
    setBusy(mode);
    setHint('Click on the map to add points. Double-click to finish. Esc to cancel.');
    try {
      await measure.execute({ mode });
      setHint(null);
    } catch (err) {
      setHint((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const startSketch = async (shape: SketchShape) => {
    if (busy) return;
    setBusy(`sketch:${shape}` as Busy);
    setHint(
      shape === 'polygon' || shape === 'polyline'
        ? 'Click to add vertices. Double-click to finish. Esc to cancel.'
        : 'Click and drag on the map. Esc to cancel.',
    );
    try {
      await sketchTool.execute({ shape });
      setHint(null);
    } catch (err) {
      setHint((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const exportPng = async () => {
    if (busy) return;
    setBusy('export');
    setHint(null);
    try {
      const result = (await printMap.execute({})) as { filename?: string };
      setHint(result?.filename ? `Saved ${result.filename}` : 'Exported');
    } catch (err) {
      setHint((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <section className="flex flex-col gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Measure
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={!!busy && busy !== 'distance'}
            onClick={() => startMeasure('distance')}
            aria-pressed={busy === 'distance'}
            className="tool-tile"
            data-active={busy === 'distance' ? 'true' : 'false'}
          >
            <Ruler className="size-4" />
            <span>Distance</span>
          </button>
          <button
            type="button"
            disabled={!!busy && busy !== 'area'}
            onClick={() => startMeasure('area')}
            aria-pressed={busy === 'area'}
            className="tool-tile"
            data-active={busy === 'area' ? 'true' : 'false'}
          >
            <Square className="size-4" />
            <span>Area</span>
          </button>
        </div>

        {measurements.length > 0 && (
          <div className="mt-1 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Results ({measurements.length})
              </div>
              <button
                type="button"
                onClick={clearMeasurements}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                title="Remove all measurements"
              >
                <Trash2 className="size-3" /> Clear all
              </button>
            </div>
            <ul className="flex flex-col gap-1">
              {measurements.map((m) => (
                <li
                  key={m.id}
                  className="group flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2 py-1.5 hover:bg-accent/50"
                >
                  <button
                    type="button"
                    onClick={() => flashMeasurement(m.id)}
                    className="flex flex-1 items-center gap-2 text-left"
                    title="Zoom & flash on map"
                  >
                    {m.mode === 'distance' ? (
                      <Ruler className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <Square className="size-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="text-[13px] font-semibold tabular-nums">{m.label}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeMeasurement(m.id)}
                    className="grid size-6 place-items-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive focus:opacity-100"
                    title="Remove this measurement"
                    aria-label="Remove measurement"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <hr className="border-border" />

      <section className="flex flex-col gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Sketch
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SKETCH_SHAPES.map(({ id, label, Icon }) => {
            const key = `sketch:${id}` as Busy;
            return (
              <button
                key={id}
                type="button"
                disabled={!!busy && busy !== key}
                onClick={() => startSketch(id)}
                aria-pressed={busy === key}
                className="tool-tile"
                data-active={busy === key ? 'true' : 'false'}
              >
                <Icon className="size-4" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {sketches.length > 0 && (
          <div className="mt-1 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Sketches ({sketches.length})
              </div>
              <button
                type="button"
                onClick={clearSketches}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                title="Remove all sketches"
              >
                <Trash2 className="size-3" /> Clear all
              </button>
            </div>
            <ul className="flex flex-col gap-1">
              {sketches.map((s) => {
                const Icon = SKETCH_ICON[s.shape];
                return (
                  <li
                    key={s.id}
                    className="group flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2 py-1.5 hover:bg-accent/50"
                  >
                    <button
                      type="button"
                      onClick={() => flashSketch(s.id)}
                      className="flex flex-1 items-center gap-2 text-left"
                      title="Zoom & flash on map"
                    >
                      <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate text-[12px] font-medium tabular-nums">
                        {s.label}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSketch(s.id)}
                      className="grid size-6 place-items-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive focus:opacity-100"
                      title="Remove this sketch"
                      aria-label="Remove sketch"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <hr className="border-border" />

      <section className="flex flex-col gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Other
        </div>
        <button
          type="button"
          disabled={!!busy && busy !== 'export'}
          onClick={exportPng}
          className="tool-btn"
        >
          <Printer className="size-3.5" /> Export PNG
        </button>
      </section>

      {hint && (
        <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">
          {hint}
        </div>
      )}

      <style>{`
        .tool-tile {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
          padding: 0.625rem 0.5rem;
          border-radius: 0.5rem;
          border: 1px solid var(--border);
          background: var(--card);
          color: var(--foreground);
          font-size: 0.75rem;
          font-weight: 500;
          transition: background-color 120ms, border-color 120ms;
        }
        .tool-tile:hover { background: var(--accent); }
        .tool-tile[data-active="true"] {
          border-color: rgb(29 145 192);
          background: rgba(29, 145, 192, 0.12);
          color: rgb(29 145 192);
        }
        .tool-tile:disabled { opacity: 0.5; cursor: not-allowed; }
        .tool-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.4rem 0.625rem;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          font-size: 0.75rem;
          background: transparent;
          color: var(--foreground);
        }
        .tool-btn:hover { background: var(--accent); }
        .tool-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
